import {
  ArrowPathIcon,
  CircleStackIcon,
  CpuChipIcon,
  EnvelopeIcon,
  ServerIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Input } from '@heroui/input';
import { Spinner } from '@heroui/spinner';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { adminService } from '../../services/adminService';
import { AdminSettings } from '../../types/admin';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const AdminSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [cacheKey, setCacheKey] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const response = await adminService.getAdminSettings();
      setSettings(response);
    } catch (error: any) {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const clearCache = async (specificKey?: string) => {
    try {
      setIsClearing(true);
      const response = await adminService.clearAdminCache(specificKey);

      const message = specificKey
        ? `Cache key '${specificKey}' cleared successfully`
        : 'All cache cleared successfully';

      toast.success(response?.message || message);
      setCacheKey('');
    } catch (error: any) {
      toast.error('Failed to clear cache');
    } finally {
      setIsClearing(false);
    }
  };

  const cleanupAbandonedOrders = async () => {
    try {
      setIsCleaning(true);
      const response = await adminService.cleanupAbandonedOrders();

      toast.success(
        `${response.message}. Cleaned: ${response.cleanedOrders} orders`
      );

      // Reload settings to refresh database stats
      await loadSettings();
    } catch (error: any) {
      toast.error('Failed to cleanup abandoned orders');
    } finally {
      setIsCleaning(false);
    }
  };

  if (isLoading) {
    return (
      <div className='flex justify-center items-center min-h-[400px]'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className='text-center py-8'>
        <p className='text-gray-500'>Failed to load settings</p>
        <Button onClick={loadSettings} className='mt-4'>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Admin Settings'
        description='System information, database stats, cache management, and maintenance tools.'
        url={generateCanonicalUrl('/admin/settings')}
        type='website'
      />
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>Admin Settings</h1>
        <Button
          onClick={loadSettings}
          startContent={<ArrowPathIcon className='w-4 h-4' />}
          variant='bordered'
        >
          Refresh
        </Button>
      </div>

      {/* System Information */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <ServerIcon className='w-5 h-5' />
            <h2 className='text-lg font-semibold'>System Information</h2>
          </div>
        </CardHeader>
        <CardBody>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <p className='text-sm text-gray-600'>Environment</p>
              <Chip
                color={
                  settings.systemInfo.environment === 'Production'
                    ? 'danger'
                    : 'warning'
                }
                variant='flat'
              >
                {settings.systemInfo.environment}
              </Chip>
            </div>
            <div>
              <p className='text-sm text-gray-600'>Machine</p>
              <p className='font-medium'>{settings.systemInfo.machineName}</p>
            </div>
            <div>
              <p className='text-sm text-gray-600'>OS Version</p>
              <p className='font-medium'>{settings.systemInfo.osVersion}</p>
            </div>
            <div>
              <p className='text-sm text-gray-600'>CPU Cores</p>
              <p className='font-medium'>
                {settings.systemInfo.processorCount}
              </p>
            </div>
            <div>
              <p className='text-sm text-gray-600'>Memory Usage</p>
              <p className='font-medium'>{settings.systemInfo.workingSet} MB</p>
            </div>
            <div>
              <p className='text-sm text-gray-600'>Last Restart</p>
              <p className='font-medium'>
                {new Date(settings.systemInfo.lastRestart).toLocaleString()}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Database Information */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <CircleStackIcon className='w-5 h-5' />
            <h2 className='text-lg font-semibold'>Database Information</h2>
          </div>
        </CardHeader>
        <CardBody>
          <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
            <div className='text-center p-4 bg-blue-50 rounded-lg'>
              <p className='text-2xl font-bold text-blue-600'>
                {settings.databaseInfo.totalOrders}
              </p>
              <p className='text-sm text-gray-600'>Total Orders</p>
            </div>
            <div className='text-center p-4 bg-green-50 rounded-lg'>
              <p className='text-2xl font-bold text-green-600'>
                {settings.databaseInfo.totalUsers}
              </p>
              <p className='text-sm text-gray-600'>Total Users</p>
            </div>
            <div className='text-center p-4 bg-purple-50 rounded-lg'>
              <p className='text-2xl font-bold text-purple-600'>
                {settings.databaseInfo.totalProducts}
              </p>
              <p className='text-sm text-gray-600'>Total Products</p>
            </div>
            <div className='text-center p-4 bg-yellow-50 rounded-lg'>
              <p className='text-2xl font-bold text-yellow-600'>
                {settings.databaseInfo.pendingOrders}
              </p>
              <p className='text-sm text-gray-600'>Pending Orders</p>
            </div>
            <div className='text-center p-4 bg-orange-50 rounded-lg'>
              <p className='text-2xl font-bold text-orange-600'>
                {settings.databaseInfo.processingOrders}
              </p>
              <p className='text-sm text-gray-600'>Processing Orders</p>
            </div>
            <div className='text-center p-4 bg-green-50 rounded-lg'>
              <p className='text-2xl font-bold text-green-600'>
                {settings.databaseInfo.completedOrders}
              </p>
              <p className='text-sm text-gray-600'>Completed Orders</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Cache Management */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <CpuChipIcon className='w-5 h-5' />
            <h2 className='text-lg font-semibold'>Cache Management</h2>
          </div>
        </CardHeader>
        <CardBody>
          <div className='space-y-4'>
            <div className='flex items-center gap-4'>
              <div>
                <p className='text-sm text-gray-600'>Cache Provider</p>
                <Chip
                  color={
                    settings.cacheInfo.isRedisAvailable ? 'success' : 'warning'
                  }
                  variant='flat'
                >
                  {settings.cacheInfo.cacheProvider}
                </Chip>
              </div>
              <div>
                <p className='text-sm text-gray-600'>Redis Status</p>
                <Chip
                  color={
                    settings.cacheInfo.isRedisAvailable ? 'success' : 'danger'
                  }
                  variant='flat'
                >
                  {settings.cacheInfo.isRedisAvailable
                    ? 'Available'
                    : 'Unavailable'}
                </Chip>
              </div>
            </div>

            <Divider />

            <div className='space-y-3'>
              <div className='flex gap-2'>
                <Input
                  placeholder='Enter specific cache key (optional)'
                  value={cacheKey}
                  onChange={e => setCacheKey(e.target.value)}
                  className='flex-1'
                />
                <Button
                  onClick={() => clearCache(cacheKey || undefined)}
                  isLoading={isClearing}
                  color='warning'
                  startContent={<TrashIcon className='w-4 h-4' />}
                >
                  {cacheKey ? 'Clear Key' : 'Clear All'}
                </Button>
              </div>
              <p className='text-xs text-gray-500'>
                Leave empty to clear all cache, or specify a key to clear
                specific cached data.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Email Configuration */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <EnvelopeIcon className='w-5 h-5' />
            <h2 className='text-lg font-semibold'>Email Configuration</h2>
          </div>
        </CardHeader>
        <CardBody>
          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <p className='text-sm text-gray-600'>Status:</p>
              <Chip
                color={
                  settings.emailConfiguration.isConfigured
                    ? 'success'
                    : 'danger'
                }
                variant='flat'
              >
                {settings.emailConfiguration.isConfigured
                  ? 'Configured'
                  : 'Not Configured'}
              </Chip>
            </div>

            {settings.emailConfiguration.isConfigured && (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
                <div>
                  <p className='text-sm text-gray-600'>SMTP Host</p>
                  <p className='font-medium'>
                    {settings.emailConfiguration.smtpHost}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-gray-600'>SMTP Port</p>
                  <p className='font-medium'>
                    {settings.emailConfiguration.smtpPort}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-gray-600'>From Email</p>
                  <p className='font-medium'>
                    {settings.emailConfiguration.fromEmail}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-gray-600'>Admin Email</p>
                  <p className='font-medium'>
                    {settings.emailConfiguration.adminEmail}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Maintenance */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <WrenchScrewdriverIcon className='w-5 h-5' />
            <h2 className='text-lg font-semibold'>Maintenance</h2>
          </div>
        </CardHeader>
        <CardBody>
          <div className='space-y-4'>
            <div>
              <h3 className='font-medium mb-2'>Cleanup Abandoned Orders</h3>
              <p className='text-sm text-gray-600 mb-3'>
                Remove orders that have been pending for more than 24 hours with
                placeholder shipping data. These are typically from incomplete
                checkout processes.
              </p>
              <Button
                onClick={cleanupAbandonedOrders}
                isLoading={isCleaning}
                color='warning'
                startContent={<TrashIcon className='w-4 h-4' />}
              >
                Cleanup Abandoned Orders
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default AdminSettingsPage;
